import React, { useState } from 'react';
import { ArrowRightIcon, HandIcon, PlusIcon } from 'lucide-react';
import { InviteDialog } from '../../components/invite/InviteDialog';
import { InitialAvatar } from '../../components/InitialAvatar';
import { Button } from '../../components/ui/Button';
import { ERA_STORIES, PANEL_PHOTOS } from '../../data/registration';
import { CURRENT_MEMBER, DECADES, arrivalsThisWeek, communityTotals, decadeCounts, membersByDecade, scaleMembers } from '../../data/community';
import { BadgeMark } from '../../components/profile/MemberBadges';
import { CollectiveTile } from '../../components/dashboard/CollectiveTile';
import { WhatsNewTile } from '../../components/dashboard/WhatsNewTile';
interface HomeMosaicProps {
  sparse: boolean;
  onOpenPeople: () => void;
}
const DECADE_PHOTOS: Record<string, {
  src: string;
  alt: string;
}> = {
  '1970s': PANEL_PHOTOS.founding,
  '1980s': PANEL_PHOTOS.building,
  '1990s': PANEL_PHOTOS.room,
  '2000s': PANEL_PHOTOS.hands,
  '2010s': PANEL_PHOTOS.era,
  '2020s': PANEL_PHOTOS.voices
};
export function HomeMosaic({
  sparse,
  onOpenPeople
}: HomeMosaicProps) {
  const [inviteOpen, setInviteOpen] = useState(false);
  const members = scaleMembers(sparse);
  const counts = decadeCounts(sparse);
  const totals = communityTotals(sparse);
  const yourPeople = membersByDecade(members, CURRENT_MEMBER.decade).filter(member => member.name !== CURRENT_MEMBER.fullName);
  const arrivals = arrivalsThisWeek(sparse);
  return <div className="flex flex-col gap-3 p-3 sm:p-4" data-id="element-660" data-mp="npq4mn60qom2">
      {/* TIME AXIS — the decade ribbon doubles as the page header. */}
      <section aria-label="Fifty years" className="overflow-hidden rounded-card bg-ink" data-id="element-661" data-mp="9qqch5jarrl5">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 px-4 pb-3 pt-4 sm:px-5" data-id="element-662" data-mp="enp5tlv4zuia">
          <h1 className="text-heading font-semibold text-on-ink" data-id="element-663" data-mp="2uel5m20wnh8p">
            {CURRENT_MEMBER.firstName}, you sit in {CURRENT_MEMBER.years}
          </h1>
          <p className="text-small text-on-ink opacity-70" data-id="element-664" data-mp="14yr693z5j4cf">
            {totals.members} members · {totals.collectiveYears} collective years represented
          </p>
        </div>

        <ol className="flex snap-x gap-px overflow-x-auto bg-ink pb-4 pl-4 pr-4 sm:pl-5" data-id="element-665" data-mp="v97oy2r37ijo">
          {DECADES.map(decade => {
          const isYours = decade === CURRENT_MEMBER.decade;
          const photo = DECADE_PHOTOS[decade];
          return <li key={decade} className="relative w-36 shrink-0 snap-start overflow-hidden rounded-field sm:w-44" data-id="element-666" data-mp="ma43wt8d0t2j">
                <img src={photo.src} alt={photo.alt} className="h-28 w-full object-cover sm:h-32" data-id="element-667" data-mp="c9ttfgdplzr2" />
                <div aria-hidden="true" className={`absolute inset-0 bg-ink ${isYours ? 'opacity-25' : 'opacity-60'}`} data-id="element-668" data-mp="26oe7ha7iyx1h" />
                <div className="absolute inset-x-0 bottom-0 flex flex-col gap-0.5 p-2.5" data-id="element-669" data-mp="22nki0cefpa6h">
                  <span className="text-small font-semibold text-on-ink" data-id="element-670" data-mp="3kjwaeq6t76ig">{decade}</span>
                  {ERA_STORIES[decade] ? <span className="line-clamp-2 text-eyebrow-caption text-on-ink opacity-90" data-id="element-671" data-mp="3ua7gojdytrsi">
                      {ERA_STORIES[decade].headline}
                    </span> : null}
                  <span className="text-eyebrow-caption font-medium uppercase text-on-ink opacity-80" data-id="element-672" data-mp="31ljr4bm6og95">
                    {counts[decade]} {counts[decade] === 1 ? 'member' : 'members'}
                  </span>
                </div>
                {isYours ? <span className="absolute left-2.5 top-2.5 inline-flex items-center rounded-pill bg-highlight px-2 py-0.5 text-eyebrow-caption font-semibold uppercase text-on-highlight" data-id="element-673" data-mp="3akoqfk5kkdmd">
                    You
                  </span> : null}
              </li>;
        })}
        </ol>
      </section>

      {/* MOSAIC — people, archive, acknowledgement and contribution at once. */}
      <div className="grid gap-3 lg:grid-cols-12" data-id="element-674" data-mp="tg13g3b33l6h">
        <section aria-labelledby="archive-tile" className="relative overflow-hidden rounded-card lg:col-span-7" data-id="element-675" data-mp="ofn6bgb0tvn8">
          <img src={PANEL_PHOTOS.founding.src} alt={PANEL_PHOTOS.founding.alt} className="h-72 w-full object-cover sm:h-80 lg:h-full lg:min-h-[19rem]" data-id="element-676" data-mp="12fxugbky4w4p" />
          <div aria-hidden="true" className="absolute inset-0 bg-ink opacity-55" data-id="element-677" data-mp="1cay5b8ekcqb7" />
          <div className="absolute inset-x-0 bottom-0 flex flex-col gap-2 p-5" data-id="element-678" data-mp="17d6gwod4nmbr">
            <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-on-ink" data-id="element-679" data-mp="1m1kikvyt54qq">
              <span aria-hidden="true" className="h-px w-5 bg-highlight" data-id="element-680" data-mp="1h4u58gee0n90" />
              Added to the archive · 1996
            </span>
            <h2 id="archive-tile" className="text-section-heading font-semibold text-on-ink" data-id="element-681" data-mp="1v0xrnnj0rrax">
              Nobody has named the people in this one yet.
            </h2>
            <p className="max-w-md text-small text-on-ink opacity-80" data-id="element-682" data-mp="29qinxaddun46">
              Friday night, outside after the meeting. You were here that year, so you may be one of the few who can
              say who&apos;s in it.
            </p>
            <div className="flex flex-wrap gap-2 pt-1" data-id="element-683" data-mp="24qwaaztn74uu">
              <Button data-id="element-684" data-mp="24pbhpfqw1vzm">Help name them</Button>
              <Button variant="secondary" data-id="element-685" data-mp="1qzrj8m4cbkw4">Skip</Button>
            </div>
          </div>
        </section>

        <section aria-labelledby="people-tile" className="flex flex-col rounded-card border border-rule bg-card lg:col-span-5" data-id="element-686" data-mp="1vpy7ulyur5ed">
          <div className="flex items-baseline justify-between gap-3 border-b border-rule px-5 py-4" data-id="element-687" data-mp="1h34z5zju8xv5">
            <h2 id="people-tile" className="text-heading font-semibold text-ink" data-id="element-688" data-mp="1m0ogj8bu1qrb">
              Here when you were
            </h2>
            <span className="text-small text-ink-muted" data-id="element-689" data-mp="1czgsdj3166ay">{CURRENT_MEMBER.years}</span>
          </div>

          {yourPeople.length > 0 ? <ul className="flex flex-col divide-y divide-rule" data-id="element-690" data-mp="3epo06ii4g2v1">
              {yourPeople.map(member => <li key={member.name} className="flex items-center gap-3 px-5 py-3" data-id="element-691" data-mp="39wmik2qj5p7r">
                  <InitialAvatar name={member.name} size={40} data-id="element-692" data-mp="34zw0xaz2ulk8" />
                  <div className="flex min-w-0 flex-1 flex-col" data-id="element-693" data-mp="30xrasm7u872c">
                    <span className="flex items-center gap-1.5 truncate text-body font-medium text-ink" data-id="element-694" data-mp="30vek9ko0kodo">
                      {member.name.split(' ')[0]} {member.name.split(' ')[1]?.charAt(0)}.
                      <BadgeMark name={member.name} data-id="element-695" data-mp="3smknwb2y147p" />
                    </span>
                    <span className="truncate text-small text-ink-muted" data-id="element-696" data-mp="3olil5k9tia0z">
                      {member.years}
                      {member.note ? ` · ${member.note}` : ''}
                    </span>
                  </div>
                  <button type="button" className="inline-flex h-11 w-11 items-center justify-center rounded-pill border border-rule text-ink-muted hover:bg-canvas hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-697" data-mp="2d5tyyv4e1f18">
                    <span className="sr-only" data-id="element-698" data-mp="wr7nrmbfsmlb">Wave at {member.name.split(' ')[0]}</span>
                    <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-699" data-mp="11ippiclaar7c" />
                  </button>
                </li>)}
            </ul> : <p className="px-5 py-6 text-small text-ink-muted" data-id="element-700" data-mp="16jdekspycxyx">
              Nobody from your years yet. We&apos;ll tell you the day someone arrives.
            </p>}

          <button type="button" onClick={onOpenPeople} className="mt-auto flex min-h-tap items-center gap-2 border-t border-rule px-5 text-left text-small font-medium text-primary-text hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-701" data-mp="1gfykdg9sqd3t">
            Everyone from the {CURRENT_MEMBER.decade}
            <ArrowRightIcon aria-hidden="true" className="h-4 w-4" data-id="element-702" data-mp="33abykmqzemuy" />
          </button>
        </section>

        <section aria-labelledby="wave-tile" className="flex flex-col justify-between gap-4 rounded-card bg-primary p-5 lg:col-span-4" data-id="element-703" data-mp="2yazbc0w77dru">
          <div className="flex flex-col gap-2" data-id="element-704" data-mp="383y2nqj2xif5">
            <span className="text-eyebrow-caption font-semibold uppercase text-on-primary opacity-80" data-id="element-705" data-mp="385959n0sgk4o">
              While you were away
            </span>
            <h2 id="wave-tile" className="text-section-heading font-semibold text-on-primary" data-id="element-706" data-mp="2ffiw7se0k8ff">
              Andre P. waved at you.
            </h2>
            <p className="text-small text-on-primary opacity-90" data-id="element-707" data-mp="2oevy9zzt5uq4">
              No message, nothing to answer. He saw your years and wanted you to know he&apos;s
              here.
            </p>
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-708" data-mp="3p6jto30oaxy">
            <Button variant="secondary" data-id="element-709" data-mp="3oez58ayjxmg">
              <HandIcon aria-hidden="true" className="h-4 w-4" data-id="element-710" data-mp="c5xdyvcn2zar" />
              Wave back
            </Button>
          </div>
        </section>

        <section aria-labelledby="arrivals-tile" className="flex flex-col rounded-card border border-rule bg-card lg:col-span-4" data-id="element-711" data-mp="g826fqrcppvt">
          <div className="flex flex-col gap-0.5 border-b border-rule px-5 py-4" data-id="element-712" data-mp="76cjax4i5d3u">
            <h2 id="arrivals-tile" className="text-heading font-semibold text-ink" data-id="element-713" data-mp="uw6s93lpahit">
              New arrivals this week
            </h2>
            <p className="text-small text-ink-muted" data-id="element-714" data-mp="zu8a5l7e4asp">
              {arrivals.count} {arrivals.count === 1 ? 'person' : 'people'} joined
              {arrivals.topDecade ? `, ${arrivals.topDecade.count} from the ${arrivals.topDecade.decade}` : ''}
            </p>
          </div>
          <ul className="flex flex-col divide-y divide-rule" data-id="element-715" data-mp="1du9b10nnni86">
            {arrivals.people.map(person => <li key={person.name} className="flex items-center gap-3 px-5 py-2.5" data-id="element-716" data-mp="1inc65d82yzqh">
                <InitialAvatar name={person.name} size={32} data-id="element-717" data-mp="1niekof363u16" />
                <span className="flex flex-1 items-center gap-1.5 truncate text-small text-ink" data-id="element-718" data-mp="1sfxwl8ce7upy">
                  {person.name}
                  <BadgeMark name={person.name} data-id="element-719" data-mp="1efdgijxv4d05" />
                </span>
                <span className="text-small text-ink-muted" data-id="element-720" data-mp="1jf03hl3ht7m5">{person.decade}</span>
              </li>)}
          </ul>
          <p className="mt-auto border-t border-rule px-5 py-3 text-small text-ink-muted" data-id="element-721" data-mp="14pkcc401v05x">
            {sparse ? 'Small, but it was twelve of us last month too.' : 'Everyone who joined, whatever decade they came from.'}
          </p>
        </section>

        <section aria-labelledby="invite-tile" className="flex flex-col justify-between gap-4 rounded-card border border-rule bg-card p-5 lg:col-span-4" data-id="element-722" data-mp="zqr00ewi1f7c">
          <div className="flex flex-col gap-2" data-id="element-723" data-mp="qqkp90s6qtbv">
            <h2 id="invite-tile" className="text-heading font-semibold text-ink" data-id="element-724" data-mp="qqb01feysq78">
              One of the two people you invited has joined.
            </h2>
            <p className="text-small text-ink-muted" data-id="element-725" data-mp="26hb94vkm1ft">
              Ken D. is in. The other invitation is still open and doesn&apos;t expire.
            </p>
          </div>
          <Button variant="secondary" onClick={() => setInviteOpen(true)} data-id="element-726" data-mp="h0vqcuc0kqp7">
            <PlusIcon aria-hidden="true" className="h-4 w-4" data-id="element-727" data-mp="c2u5c3v12hb3" />
            Invite someone from your years
          </Button>
        </section>

        <section aria-labelledby="thin-years" className="flex flex-col gap-4 rounded-card border border-rule bg-card p-5 sm:flex-row sm:items-center sm:justify-between lg:col-span-12" data-id="element-728" data-mp="l53h0x218gq2">
          <div className="flex max-w-xl flex-col gap-1.5" data-id="element-729" data-mp="uud7k8h89rc2">
            <span className="flex items-center gap-2 text-eyebrow-caption font-semibold uppercase text-ink-muted" data-id="element-730" data-mp="uwzfi98168ut">
              <span aria-hidden="true" className="h-px w-5 bg-highlight" data-id="element-731" data-mp="14t2p2057p9rs" />
              Thin years
            </span>
            <h2 id="thin-years" className="text-section-heading font-semibold text-ink" data-id="element-732" data-mp="1bwsgfg41n421">
              1994 has two entries in it.
            </h2>
            <p className="text-small text-ink-muted" data-id="element-733" data-mp="1gsznjc86ellk">
              You were here that year. Anything you write about it isn&apos;t one account among
              many. It&apos;s one of two.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3" data-id="element-734" data-mp="1umik6spsb063">
            <ul aria-hidden="true" className="hidden gap-2 sm:flex" data-id="element-735" data-mp="1ulz5dkvfkrwa">
              {[PANEL_PHOTOS.room, PANEL_PHOTOS.hands].map(photo => <li key={photo.src} className="h-16 w-16 overflow-hidden rounded-field" data-id="element-736" data-mp="24ky1k3z0ev2e">
                  <img src={photo.src} alt="" className="h-full w-full object-cover" data-id="element-737" data-mp="29jrenvadwk21" />
                </li>)}
              <li className="flex h-16 w-16 items-center justify-center rounded-field border border-dashed border-rule text-small text-ink-muted" data-id="element-738" data-mp="a76y5m79x7fe">
                +
              </li>
            </ul>
            <Button data-id="element-739" data-mp="2sgjgnyby32g0">Add something from 1994</Button>
          </div>
        </section>

        <div className="lg:col-span-8" data-id="element-740" data-mp="2npjm2zqaswgo">
          <CollectiveTile sparse={sparse} data-id="element-741" data-mp="2noi1o4ne6xma" />
        </div>

        <div className="lg:col-span-4" data-id="element-742" data-mp="2jng8a4g5pfs8">
          <WhatsNewTile data-id="element-743" data-mp="2johjc8x6a29y" />
        </div>
      </div>

      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} data-id="element-744" data-mp="2eov988cn525d" />
    </div>;
}